import { ApiButton, Box, Button, ChangeRequestForm, Grid, Typography } from 'web-library';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { useHistory } from 'react-router-dom';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import {
  AdminDetailsResponseContentObject,
  UserGroupDetailsResponse,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { ConfigurationType } from 'web-library/models/DropDown';
import { MutationStatus } from 'react-query';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';
import { mapApproversToDropdown } from './index';
import RouteKey from '../../../routes/key';
import { useGetRequestFileConfigs } from '../../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';

const { admin } = RouteKey.child;
const { userGroupAccessConfiguration } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  viewTransactionLog: {
    marginTop: theme.spacing(6),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      paddingLeft: 0,
    },
  },
  updatedTransactionContainer: {
    spacing: 0,
    flexDirection: 'row',
    justify: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  backToListingButton: {
    marginBottom: theme.spacing(7),
    height: 'fit-content',
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
      height: 'fit-content',
    },
  },
  confirmChangesButton: {
    height: 'fit-content',
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

interface ChangeRequestContentProps {
  isCreate: boolean;
  isView: boolean;
  formHook: UseFormReturn;
  errorMessage: string;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  uploadFileErrorMessage: string;
  selectedAdminDetails: AdminDetailsResponseContentObject[];
  setOpenTransactionLogModal: React.Dispatch<React.SetStateAction<boolean>>;
  userGroupDetails?: UserGroupDetailsResponse;
  postSearchApproversData?: ConfigurationType[];
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  apiButtonStatus: MutationStatus;
}

const ChangeRequestContent = ({
  isCreate,
  isView,
  formHook,
  errorMessage,
  setErrorMessage,
  uploadFileErrorMessage,
  selectedAdminDetails,
  setOpenTransactionLogModal,
  userGroupDetails,
  postSearchApproversData,
  files,
  setFiles,
  apiButtonStatus,
}: ChangeRequestContentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles();

  const history = useHistory();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: requestFilesConfigData } = useGetRequestFileConfigs({});

  const handleBackToListing = () => history.push(`/${admin}/${userGroupAccessConfiguration.route}`);

  const displayErrorMessage = () => {
    if (selectedAdminDetails.length) {
      setErrorMessage('');
    } else {
      setErrorMessage(t('admin->>UserGroups.NoAdminAddedErrorMessage'));
    }
  };

  const handleOpenTransactionLogModal = () => setOpenTransactionLogModal(true);

  return (
    <>
      {!isView && (
        <>
          <ChangeRequestForm
            formHook={formHook}
            approverDropdowns={mapApproversToDropdown(postSearchApproversData)}
          />
          {requestFilesConfigData && (
            <Grid container spacing={0}>
              <Grid md={6} sm={12} xs={12}>
                <RequestFileDropZone
                  fileConfig={requestFilesConfigData}
                  files={files}
                  setFiles={setFiles}
                  isLoading={apiButtonStatus === 'loading'}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}
      {!isCreate && userGroupDetails && (
        <TransactionAuditLogUpdatedDetails
          updatedAt={userGroupDetails.userGroupDetail.updatedAt}
          updatedBy={userGroupDetails.userGroupDetail.updatedBy}
          handleOpenLogModal={handleOpenTransactionLogModal}
          label={t('admin->>UserGroups.ViewTransactionLog')}
        />
      )}
      {!isView && (
        <>
          {errorMessage && (
            <Typography className={classes.errorText} variant="body2">
              {errorMessage}
            </Typography>
          )}
          {uploadFileErrorMessage && (
            <Typography className={classes.errorText} variant="body2">
              {uploadFileErrorMessage}
            </Typography>
          )}
          <Box className={classes.buttonContainer}>
            <Button
              id="button-create-edit-user-group-cancel"
              className={classes.cancelButton}
              variant="outlined"
              onClick={handleBackToListing}
              fullWidth={isMobile}
            >
              {t('announcementsNotifications->>CreateTemplate.Cancel')}
            </Button>

            <ApiButton
              id="button-confirm-changes-create-edit-user-group"
              className={classes.confirmChangesButton}
              status={apiButtonStatus}
              type="submit"
              fullWidth={isMobile}
              onClick={displayErrorMessage}
            >
              {t('admin->>UserGroups.ConfirmChanges')}
            </ApiButton>
          </Box>
        </>
      )}
      {isView && (
        <Button
          id="button-user-group-access-configuration-back-to-listing"
          className={classes.backToListingButton}
          onClick={handleBackToListing}
          variant="outlined"
        >
          {t('admin->>UserGroups.BackToListing')}
        </Button>
      )}
    </>
  );
};

export default ChangeRequestContent;
